/* istanbul ignore file */

import React from 'react';
import { Provider } from 'react-redux';
import { getFeatureFlags } from '@noths/polaris-client-feature-flags';
import { setLocaleCookies } from '@noths/polaris-client-localisation';
import { BaseApp, BaseAppProps } from '@noths/polaris-client-next-components';
import { SkipLinks, useSmartAppBanner } from '@noths/polaris-client-site-chrome-ui';
import { FavouritesContainer } from '@noths/polaris-client-user-favourites';
import { Merge } from '@noths/polaris-dev-ts-recipes';
import { AppContextPlusCookies } from '@noths/polaris-dev-ts-types';
import { Store } from '@reduxjs/toolkit';
import { ConnectedRouter } from 'connected-next-router';
import NextApp, { AppInitialProps, AppProps } from 'next/app';
import getConfig from 'next/config';

import { ReduxApplicationState } from 'src/redux/combinedReducer';
import { useStore } from 'src/redux/store';
import { NextConfig, PageProps } from 'src/types';
import { CONTENT_SECTION_ID } from './[partnerId]/product/[productId]';

const nextConfig = getConfig() as NextConfig;

const AppComponent = BaseApp(nextConfig);

type CustomAppProps = Merge<
  BaseAppProps & AppProps,
  {
    pageProps: PageProps;
    mvtAssignmentsHeader?: string;
  }
>;

const App = ({ experiments, ...props }: CustomAppProps) => {
  const { updatedReduxState, topBannerData } = props.pageProps || {};
  const store = useStore(props, updatedReduxState as ReduxApplicationState);
  useSmartAppBanner();

  return (
    <Provider store={store as Store}>
      <ConnectedRouter>
        <SkipLinks links={[{ content: 'Skip to content', sectionId: CONTENT_SECTION_ID }]} />
        <FavouritesContainer nextConfig={nextConfig} />
        <AppComponent {...props} topBannerData={topBannerData} experiments={experiments} />
      </ConnectedRouter>
    </Provider>
  );
};

/*
  Necessary to disable next's Automatic Static Optimization feature.
  Without it this page will be statically compiled.
*/
App.getInitialProps = async (
  appContext: AppContextPlusCookies,
): Promise<AppInitialProps & BaseAppProps> => {
  const {
    ctx: { req, res, query },
  } = appContext;
  const featureFlags = (req && getFeatureFlags(req)) || {};

  if (req && res) {
    setLocaleCookies({ req, res, query });
  }

  const appProps = await NextApp.getInitialProps(appContext);

  return { ...appProps, featureFlags };
};

export default App;
